import React, { useState, useEffect } from "react";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Star } from "lucide-react";
import { toast } from "sonner";

export default function ReviewBox({ orderId, onSubmitted }) {
  const { t } = useLang();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    api.get(`/orders/${orderId}/review`).then(r => setExisting(r.data)).catch(() => {});
  }, [orderId]);

  const submit = async () => {
    if (rating < 1) { toast.error(t("pickRating")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/review`, { rating, comment: comment || null });
      setExisting(data);
      toast.success(t("thanksReview"));
      onSubmitted?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (existing) {
    return (
      <div className="card-surface p-4" data-testid="existing-review">
        <div className="label-caps">{t("yourReview")}</div>
        <div className="flex items-center gap-1 mt-1">
          {[1,2,3,4,5].map((i) => (
            <Star key={i} className={`h-4 w-4 ${i <= existing.rating ? "fill-amber-400 text-amber-400" : "text-zinc-300"}`} />
          ))}
        </div>
        {existing.comment && <p className="text-sm text-zinc-700 mt-2">{existing.comment}</p>}
      </div>
    );
  }

  return (
    <div className="card-surface p-4" data-testid="review-box">
      <div className="label-caps">{t("rateMerchant")}</div>
      <div className="flex items-center gap-1 mt-2">
        {[1,2,3,4,5].map((i) => (
          <button
            type="button"
            key={i}
            data-testid={`star-${i}`}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(0)}
            onClick={() => setRating(i)}
            className="p-1"
          >
            <Star className={`h-6 w-6 transition ${i <= (hover || rating) ? "fill-amber-400 text-amber-400" : "text-zinc-300"}`} />
          </button>
        ))}
      </div>
      <textarea
        data-testid="review-comment"
        rows={2}
        placeholder={t("optionalComment")}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        className="mt-3 w-full rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-3 py-2 text-sm"
      />
      <button data-testid="submit-review" onClick={submit} disabled={busy || rating < 1} className="btn-primary mt-3 w-full">
        {busy ? "..." : t("submitReview")}
      </button>
    </div>
  );
}
